import api from "../..";
import type { ApiResponse } from "../../types";
import {
  EXPORT_ENDPOINTS,
  type ExportProgressUpdate,
  type ExportRequestBody,
  type ExportResultResponse,
  type ExportType,
  type RunExportOptions,
} from "./types";

const EXPORT_RESULTS_ENDPOINT = "/admin/export/results/";
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 30;
const EXPORT_RESULTS_TIMEOUT_MS = 120000;
const PROGRESS_TICK_MS = 1000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function extractTaskId(payload: unknown): string | null {
  if (typeof payload === "string") {
    const trimmed = payload.trim();
    return trimmed || null;
  }

  if (!payload || typeof payload !== "object") {
    return null;
  }

  const data = payload as Record<string, unknown>;

  if (typeof data.taskID === "string" && data.taskID.trim()) {
    return data.taskID.trim();
  }

  if (typeof data.task_id === "string" && data.task_id.trim()) {
    return data.task_id.trim();
  }

  if (typeof data.id === "string" && data.id.trim()) {
    return data.id.trim();
  }

  return null;
}

function getDownloadUrl(result: ExportResultResponse): string | null {
  return (
    result.file_url ||
    result.download_url ||
    result.url ||
    result.file ||
    null
  );
}

function normalizeExportResult(payload: unknown): ExportResultResponse | Blob {
  if (payload instanceof Blob) {
    return payload;
  }

  if (!payload || typeof payload !== "object") {
    return {};
  }

  return payload as ExportResultResponse;
}

function isCompletedStatus(status?: string): boolean {
  const normalized = status?.toLowerCase();
  return (
    normalized === "completed" ||
    normalized === "done" ||
    normalized === "success" ||
    normalized === "ready"
  );
}

function isFailedStatus(status?: string): boolean {
  const normalized = status?.toLowerCase();
  return normalized === "failed" || normalized === "error";
}

function isPendingStatus(status?: string): boolean {
  const normalized = status?.toLowerCase();
  return (
    !status ||
    normalized === "pending" ||
    normalized === "processing" ||
    normalized === "running" ||
    normalized === "in_progress"
  );
}

async function parseExportResultsResponse(
  data: Blob,
  contentType: string,
): Promise<ExportResultResponse | Blob> {
  const isJsonResponse =
    contentType.includes("application/json") ||
    contentType.includes("text/json");

  if (!isJsonResponse) {
    return data;
  }

  const text = await data.text();
  const parsed = JSON.parse(text) as ApiResponse<ExportResultResponse>;

  if (parsed.success === false) {
    throw new Error(parsed.message || "دریافت خروجی با خطا مواجه شد");
  }

  return normalizeExportResult(parsed.data);
}

async function downloadFromUrl(url: string, fileName = "export.csv"): Promise<void> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error("Download failed");
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}

async function downloadBlob(blob: Blob, fileName = "export.csv"): Promise<void> {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}

export const initiateExport = async (
  type: ExportType,
  body: ExportRequestBody,
): Promise<string> => {
  const response = await api.post<ApiResponse<unknown>>(
    EXPORT_ENDPOINTS[type],
    body,
  );

  const taskId = extractTaskId(response.data.data);

  if (!taskId) {
    throw new Error("شناسه خروجی از سرور دریافت نشد");
  }

  return taskId;
};

export const getExportResults = async (
  taskId: string,
): Promise<ExportResultResponse | Blob> => {
  const response = await api.post(EXPORT_RESULTS_ENDPOINT, { taskID: taskId }, {
    responseType: "blob",
    timeout: EXPORT_RESULTS_TIMEOUT_MS,
    validateStatus: (status) => status >= 200 && status < 300,
  });

  return parseExportResultsResponse(
    response.data as Blob,
    String(response.headers["content-type"] || ""),
  );
};

const EXPORT_PROGRESS_MESSAGES = {
  submitting: "در حال ثبت درخواست خروجی...",
  processing: "در حال تهیه و آماده‌سازی فایل...",
  downloading: "در حال دریافت و دانلود فایل...",
} as const;

function notifyProgress(
  onProgress: RunExportOptions["onProgress"],
  update: ExportProgressUpdate,
) {
  onProgress?.(update);
}

function formatProcessingMessage(elapsedSeconds: number): string {
  return `${EXPORT_PROGRESS_MESSAGES.processing} (${elapsedSeconds} ثانیه)`;
}

function startProcessingTicker(
  onProgress: RunExportOptions["onProgress"],
  startedAt: number,
) {
  notifyProgress(onProgress, {
    stage: "processing",
    message: formatProcessingMessage(0),
  });

  return window.setInterval(() => {
    const elapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);
    notifyProgress(onProgress, {
      stage: "processing",
      message: formatProcessingMessage(elapsedSeconds),
    });
  }, PROGRESS_TICK_MS);
}

async function pollExportResults(
  taskId: string,
  onProgress?: RunExportOptions["onProgress"],
): Promise<void> {
  const startedAt = Date.now();
  const progressTicker = startProcessingTicker(onProgress, startedAt);

  try {
    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
      const result = await getExportResults(taskId);

      if (result instanceof Blob) {
        window.clearInterval(progressTicker);
        notifyProgress(onProgress, {
          stage: "downloading",
          message: EXPORT_PROGRESS_MESSAGES.downloading,
        });
        await downloadBlob(result);
        return;
      }

      const downloadUrl = getDownloadUrl(result);

      if (downloadUrl) {
        window.clearInterval(progressTicker);
        notifyProgress(onProgress, {
          stage: "downloading",
          message: EXPORT_PROGRESS_MESSAGES.downloading,
        });
        await downloadFromUrl(downloadUrl);
        return;
      }

      if (isFailedStatus(result.status)) {
        throw new Error("تهیه خروجی با خطا مواجه شد");
      }

      if (isPendingStatus(result.status)) {
        await sleep(POLL_INTERVAL_MS);
        continue;
      }

      if (isCompletedStatus(result.status) && !downloadUrl) {
        throw new Error("لینک دانلود خروجی دریافت نشد");
      }

      await sleep(POLL_INTERVAL_MS);
    }

    throw new Error("زمان انتظار برای آماده‌سازی خروجی به پایان رسید");
  } finally {
    window.clearInterval(progressTicker);
  }
}

export const runExport = async (
  type: ExportType,
  body: ExportRequestBody,
  options: RunExportOptions = {},
): Promise<void> => {
  notifyProgress(options.onProgress, {
    stage: "submitting",
    message: EXPORT_PROGRESS_MESSAGES.submitting,
  });

  const taskId = await initiateExport(type, body);

  await pollExportResults(taskId, options.onProgress);
};

export function buildExportBody(
  fields: string[],
  extraFilters: Record<
    string,
    string | number | number[] | undefined
  > = {},
): ExportRequestBody {
  return {
    fields,
    ...extraFilters,
  };
}
